import React, { useState, useMemo } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

/**
 * Admin — Import récap papier.
 *
 * Le télépro a bossé sur papier (pas de laptop, Wi-Fi HS, onboarding raté…)
 * et envoie son récap texte à l'admin. L'admin le colle ici, prévisualise
 * les statuts détectés + les entreprises matchées, corrige, puis commit
 * l'import → mêmes side-effects que si le télépro avait cliqué dans le
 * portail (calls + feedback DB + CRM history + session rétroactive).
 *
 * Remplace l'ancien script one-off `scripts/import_dona_recap_*.py`.
 */

const STATUS_LABELS = {
  no_answer: "Pas de réponse",
  voicemail: "Messagerie",
  wrong_number: "Faux numéro",
  closed: "Fermée",
  see_headquarters: "Voir siège",
  contact_reached: "Contact ok",
  wrong_person: "Mauvaise personne",
  callback: "Rappel",
  interested: "Intéressé",
  meeting: "RDV",
  site_web_besoin: "Site web",
  sale: "Vente",
  refused: "Refus",
  refused_final: "Refus définitif",
};

const ALL_STATUSES = Object.keys(STATUS_LABELS);

const MATCH_LABELS = {
  list_siret: "SIRET (liste)",
  list_phone: "Tél (liste)",
  list_name: "Nom (liste)",
  db_siret: "SIRET (BDD)",
};

export default function PaperRecapImportView({ telepros = [] }) {
  const [text, setText] = useState("");
  const [teleproId, setTeleproId] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const activeTelepros = useMemo(
    () => telepros.filter((t) => t.active !== false),
    [telepros],
  );

  const doPreview = async () => {
    if (!text.trim()) {
      toast.error("Colle d'abord le texte du récap.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await api.post("/admin/telepro/paper-recap/preview", {
        text, telepro_id: teleproId || undefined, date,
      });
      setPreview(r.data);
      if (!r.data.stats?.matched) {
        toast.warning(`${r.data.stats?.total_lines || 0} ligne(s) — 0 matché`);
      } else {
        toast.success(`${r.data.stats.matched}/${r.data.stats.total_lines} entrées matchées`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Parsing impossible");
    } finally {
      setLoading(false);
    }
  };

  const updateEntry = (idx, patch) => {
    setPreview((p) => ({
      ...p,
      entries: p.entries.map((e, i) => (i === idx ? { ...e, ...patch } : e)),
    }));
  };

  const doCommit = async () => {
    if (!teleproId) {
      toast.error("Sélectionne d'abord le télépro.");
      return;
    }
    const commit = preview.entries.filter((e) => e.matched && e.business_id);
    if (!commit.length) {
      toast.error("Aucune ligne matchée à importer.");
      return;
    }
    if (!window.confirm(
      `Importer ${commit.length} appel(s) pour ${activeTelepros.find((t) => t.id === teleproId)?.code || "?"} ` +
      `sur la date ${date} ? (les 15 statuts déclenchent la boucle feedback DB + historique CRM + session rétroactive)`,
    )) return;

    setCommitting(true);
    setResult(null);
    try {
      const r = await api.post("/admin/telepro/paper-recap/import", {
        telepro_id: teleproId,
        list_id: preview.list_id,
        date,
        entries: commit,
      });
      setResult(r.data);
      toast.success(`✅ ${r.data.inserted} appels · ${r.data.opportunities} opportunité(s) · ${r.data.history_events} events CRM`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import impossible");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="paper-recap-panel">
      <div className="bg-primary/5 border-l-4 border-primary rounded-sm p-4 text-sm">
        <div className="font-medium text-primary mb-1">📝 Import de récap papier</div>
        <p className="text-muted-foreground leading-relaxed">
          Un télépro t'a envoyé son récap manuscrit (ou tapé ligne par ligne) parce qu'il a bossé sans laptop ?
          Colle le texte ci-dessous. Le parseur reconnaît automatiquement <b>SIRET</b>, <b>téléphone</b>, <b>date de rappel</b> et
          les <b>15 statuts</b> à partir des mots-clés (refus, messagerie, rappel, intéressé, RDV, faux num, siège, fermée…).
          Chaque ligne = 1 appel.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Télépro</label>
          <select
            value={teleproId}
            onChange={(e) => setTeleproId(e.target.value)}
            data-testid="paper-recap-telepro-select"
            className="mt-1 w-full text-sm border border-border rounded-sm px-2 py-2 bg-background"
          >
            <option value="">— Choisir —</option>
            {activeTelepros.map((t) => (
              <option key={t.id} value={t.id}>
                {t.code} — {t.first_name} {t.last_name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="paper-recap-date-input"
            className="mt-1 w-full text-sm border border-border rounded-sm px-2 py-2 bg-background"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={doPreview}
            disabled={loading}
            data-testid="paper-recap-preview-btn"
            className="w-full bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold rounded-sm hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "Analyse…" : "📄 Analyser le récap"}
          </button>
        </div>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={"Une entrée par ligne. Exemples :\n85206379100028 Fermée définitivement\n07 53 30 48 29 — Faux numéro\nBOULANGERIE MARTIN — Refus\n51205798500040 Rappel jeudi matin avant 11h 2026-09-10\nNAVITOUR — Relancer le 8 octobre 2026"}
        rows={12}
        data-testid="paper-recap-textarea"
        className="w-full text-sm font-mono border border-border rounded-sm p-3 bg-background"
      />

      {preview && (
        <div className="border border-border rounded-sm bg-card" data-testid="paper-recap-preview">
          <div className="flex flex-wrap items-center justify-between gap-2 p-3 border-b border-border">
            <div className="text-sm">
              <b>{preview.stats.matched}</b> / {preview.stats.total_lines} matchées ·
              {preview.stats.unmatched > 0 && (
                <span className="text-secondary"> {preview.stats.unmatched} non matchées</span>
              )}
              {preview.list_id && (
                <span className="text-muted-foreground text-xs ml-2">liste {preview.list_id.slice(0, 8)}…</span>
              )}
            </div>
            <button
              onClick={doCommit}
              disabled={committing || !teleproId}
              data-testid="paper-recap-commit-btn"
              className="bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold rounded-sm hover:opacity-90 disabled:opacity-50"
            >
              {committing ? "Import…" : `✅ Importer ${preview.entries.filter((e) => e.matched).length} appel(s)`}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-muted/50 text-muted-foreground uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">#</th>
                  <th className="text-left px-3 py-2">Ligne</th>
                  <th className="text-left px-3 py-2">Statut</th>
                  <th className="text-left px-3 py-2">Match</th>
                  <th className="text-left px-3 py-2">Rappel</th>
                </tr>
              </thead>
              <tbody>
                {preview.entries.map((e, i) => (
                  <tr
                    key={i}
                    className={"border-t border-border " + (e.matched ? "" : "bg-secondary/5")}
                    data-testid={`paper-recap-row-${i}`}
                  >
                    <td className="px-3 py-2 text-muted-foreground">{e.index}</td>
                    <td className="px-3 py-2 max-w-[380px]">
                      <div className="truncate" title={e.line}>{e.line}</div>
                      {e.matched ? (
                        <div className="text-[10px] text-muted-foreground">
                          → <b>{e.business_name}</b> {e.business_city && `· ${e.business_city}`}{" "}
                          <span className="opacity-60">({MATCH_LABELS[e.match_kind] || e.match_kind})</span>
                        </div>
                      ) : (
                        <div className="text-[10px] text-secondary">⚠ Aucune fiche trouvée — sera ignorée</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={e.status}
                        onChange={(ev) => updateEntry(i, { status: ev.target.value })}
                        className="text-xs border border-border rounded-sm px-1 py-0.5 bg-background"
                        data-testid={`paper-recap-status-${i}`}
                      >
                        {ALL_STATUSES.map((s) => (
                          <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {e.matched ? "✓" : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground tabular-nums">
                      {e.callback_at ? e.callback_at.slice(0, 16).replace("T", " ") : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-primary/10 border-l-4 border-primary rounded-sm p-4" data-testid="paper-recap-result">
          <div className="font-medium text-primary mb-1">✅ Import réussi</div>
          <ul className="text-sm text-muted-foreground space-y-0.5">
            <li>• <b>{result.inserted}</b> appels insérés/mis à jour</li>
            <li>• <b>{result.biz_updated}</b> fiches BDD nettoyées (faux num, fermetures, ownership…)</li>
            <li>• <b>{result.history_events}</b> événements CRM historiques</li>
            <li>• <b>{result.contact_reached}</b> contacts humains · <b>{result.opportunities}</b> opportunités créées</li>
            {result.skipped?.length > 0 && (
              <li className="text-secondary">
                ⚠ {result.skipped.length} ligne(s) sautées (fiche non trouvée)
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
